import { format } from 'date-fns';
import { Sampling } from 'src/shared/types/sampling';

// example of timelines
// sample 1: sampling.stage = approved
// [
//   {
//       "stage": "requested",
//       "started_at": "2026-01-15T08:50:05.455345Z",
//       "finished_at": "2026-01-15T08:50:07.733394Z",
//       "client_notes": null,
//       "following_stage": null,
//       "next_stage_is_final": null,
//       "estimated_started_at": "2026-01-15T08:50:05.455345Z",
//       "estimated_finished_at": "2026-01-15T08:50:05.455345Z"
//   },
//   {
//       "stage": "paid",
//       "started_at": "2026-01-15T08:50:07.733394Z",
//       "finished_at": "2026-02-02T13:48:00Z",
//       "client_notes": null,
//       "following_stage": null,
//       "next_stage_is_final": null,
//       "estimated_started_at": "2026-01-15T08:50:07.733394Z",
//       "estimated_finished_at": "2026-01-16T08:50:07.733395Z"
//   },
//   {
//       "stage": "approved",
//       "started_at": "2026-02-02T13:48:00Z",
//       "finished_at": "2026-02-02T13:48:00Z",
//       "client_notes": "### Sample has been approved\r\n\r\nSample will be shipped in a few days.",
//       "following_stage": null,
//       "next_stage_is_final": true,
//       "estimated_started_at": "2026-02-02T13:48:00Z",
//       "estimated_finished_at": "2026-02-03T15:29:28.424202Z"
//   }
// ]
// sample 1 needs to return 2026-02-02T13:48:00Z because it has finished_at property

// sample 2: sampling.stage = paid
// [
//   {
//       "stage": "requested",
//       "started_at": "2026-01-15T08:50:05.455345Z",
//       "finished_at": "2026-01-15T08:50:07.644416Z",
//       "client_notes": null,
//       "following_stage": null,
//       "next_stage_is_final": null,
//       "estimated_started_at": "2026-01-15T08:50:05.455345Z",
//       "estimated_finished_at": "2026-01-15T08:50:05.455345Z"
//   },
//   {
//       "stage": "paid",
//       "started_at": "2026-01-15T08:50:07.644416Z",
//       "finished_at": null,
//       "client_notes": null,
//       "following_stage": null,
//       "next_stage_is_final": null,
//       "estimated_started_at": "2026-01-15T08:50:07.644416Z",
//       "estimated_finished_at": "2026-01-31T09:47:33.357613Z"
//   },
//   {
//       "stage": "production-started",
//       "started_at": null,
//       "finished_at": null,
//       "client_notes": null,
//       "following_stage": null,
//       "next_stage_is_final": null,
//       "estimated_started_at": "2026-01-31T09:47:33.357613Z",
//       "estimated_finished_at": "2026-01-31T09:47:33.357613Z"
//   },
//   {
//       "stage": "production-completed",
//       "started_at": null,
//       "finished_at": null,
//       "client_notes": null,
//       "following_stage": null,
//       "next_stage_is_final": null,
//       "estimated_started_at": "2026-01-31T09:47:33.357613Z",
//       "estimated_finished_at": "2026-02-02T09:47:33.357613Z"
//   },
//   {
//       "stage": "qa-started",
//       "started_at": null,
//       "finished_at": null,
//       "client_notes": null,
//       "following_stage": null,
//       "next_stage_is_final": null,
//       "estimated_started_at": "2026-02-02T09:47:33.357613Z",
//       "estimated_finished_at": "2026-02-03T09:47:33.357613Z"
//   },
//   {
//       "stage": "qa-completed",
//       "started_at": null,
//       "finished_at": null,
//       "client_notes": null,
//       "following_stage": null,
//       "next_stage_is_final": null,
//       "estimated_started_at": "2026-02-03T09:47:33.357613Z",
//       "estimated_finished_at": "2026-02-04T09:47:33.357613Z"
//   },
//   {
//       "stage": "approved",
//       "started_at": null,
//       "finished_at": null,
//       "client_notes": null,
//       "following_stage": null,
//       "next_stage_is_final": null,
//       "estimated_started_at": "2026-02-04T09:47:33.357613Z",
//       "estimated_finished_at": "2026-02-05T09:47:33.357613Z"
//   },
//   {
//       "stage": "sent",
//       "started_at": null,
//       "finished_at": null,
//       "client_notes": null,
//       "following_stage": null,
//       "next_stage_is_final": null,
//       "estimated_started_at": "2026-02-05T09:47:33.357613Z",
//       "estimated_finished_at": "2026-02-05T09:47:33.357613Z"
//   },
//   {
//       "stage": "delivered",
//       "started_at": null,
//       "finished_at": null,
//       "client_notes": null,
//       "following_stage": null,
//       "next_stage_is_final": null,
//       "estimated_started_at": "2026-02-05T09:47:33.357613Z",
//       "estimated_finished_at": "2026-02-05T09:47:33.357613Z"
//   }
// ]
// sample 2 returns 2026-02-05T09:47:33.357613Z it bases it off estimated_finished_at because it has no finished_at property

type CompletedDateResult = {
  date: string;
  is_expected: boolean;
};

const getCompletedDate = (sampling: Sampling): CompletedDateResult => {
  if (sampling.stage?.includes('rejected')) {
    return { date: 'Cancelled', is_expected: false };
  }

  const lastStage = sampling?.timeline?.at(-1);

  if (lastStage?.finished_at) {
    return {
      date: format(lastStage.finished_at, 'MMM dd, yyyy'),
      is_expected: false
    };
  }

  if (lastStage?.estimated_finished_at) {
    return {
      date: format(lastStage.estimated_finished_at, 'MMM dd, yyyy'),
      is_expected: true
    };
  }

  return {
    date: format(sampling.estimated_end_date, 'MMM dd, yyyy'),
    is_expected: true
  };
};

export default getCompletedDate;
